import { supabase } from '../config/supabase';
import { PreOnboardingLead, FrontendSMS } from '../types/sms';

export const frontendSMSService = {
  /**
   * Get all pre-onboarding leads for the current user's organization
   * Only owners and admins can access this
   */
  async getLeads(): Promise<PreOnboardingLead[]> {
    try {
      // Get current user and verify permissions
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user profile to check role and org_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, role')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('Could not fetch user profile');
      }

      // Verify user has appropriate role
      if (profile.role !== 'owner' && profile.role !== 'admin') {
        throw new Error('Access denied: Only owners and admins can view leads');
      }

      // Fetch leads for the user's organization
      const { data, error } = await supabase
        .from('pre_onboarding_leads')
        .select('*')
        .eq('org_id', profile.org_id)
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      // Remove duplicates based on phone_number, keeping the most recent one
      // This ensures each unique phone number appears only once in the leads list
      const uniqueLeads = (data || []).reduce((acc: PreOnboardingLead[], current: PreOnboardingLead) => {
        // Skip leads without phone numbers for deduplication
        if (!current.phone_number) {
          acc.push(current);
          return acc;
        }
        
        const existingLead = acc.find(lead => 
          lead.phone_number && 
          lead.phone_number === current.phone_number
        );
        
        if (!existingLead) {
          // No duplicate found, add the current lead
          acc.push(current);
        } else {
          // Duplicate found, keep the one with the most recent created_at
          if (new Date(current.created_at) > new Date(existingLead.created_at)) {
            // Replace the existing lead with the more recent one
            const index = acc.findIndex(lead => 
              lead.phone_number && 
              lead.phone_number === current.phone_number
            );
            acc[index] = current;
          }
        }
        
        return acc;
      }, []);

      return uniqueLeads;

    } catch (error: any) {
      console.error('Error fetching leads:', error);
      throw error;
    }
  },

  /**
   * Get a specific lead by ID
   */
  async getLeadById(leadId: string): Promise<PreOnboardingLead | null> {
    try {
      // Get current user and verify permissions
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user profile to check role and org_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, role')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('Could not fetch user profile');
      }

      // Verify user has appropriate role
      if (profile.role !== 'owner' && profile.role !== 'admin') {
        throw new Error('Access denied: Only owners and admins can view leads');
      }

      // Fetch the specific lead
      const { data, error } = await supabase
        .from('pre_onboarding_leads')
        .select('*')
        .eq('id', leadId)
        .eq('org_id', profile.org_id)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return null; // Lead not found
        }
        throw error;
      }

      return data;

    } catch (error: any) {
      console.error('Error fetching lead:', error);
      throw error;
    }
  },

  /**
   * Update lead information
   */
  async updateLead(leadId: string, updates: Partial<PreOnboardingLead>): Promise<PreOnboardingLead> {
    try {
      // Get current user and verify permissions
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user profile to check role and org_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, role')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('Could not fetch user profile');
      }

      // Verify user has appropriate role
      if (profile.role !== 'owner' && profile.role !== 'admin') {
        throw new Error('Access denied: Only owners and admins can update leads');
      }

      // Update the lead
      const { data, error } = await supabase
        .from('pre_onboarding_leads')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', leadId)
        .eq('org_id', profile.org_id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;

    } catch (error: any) {
      console.error('Error updating lead:', error);
      throw error;
    }
  },

  /**
   * Mark follow-up as sent for a lead
   */
  async markFollowUpSent(leadId: string): Promise<void> {
    try {
      await this.updateLead(leadId, { follow_up_sent: true });
    } catch (error) {
      console.error('Error marking follow-up as sent:', error);
      throw error;
    }
  },

  /**
   * Mark JotForm as filled for a lead
   */
  async markJotFormFilled(leadId: string): Promise<void> {
    try {
      await this.updateLead(leadId, { jotform_filled: true });
    } catch (error) {
      console.error('Error marking JotForm as filled:', error);
      throw error;
    }
  },

  /**
   * Log an outbound SMS message to the frontend_sms table
   */
  async logOutboundSMS(
    name: string,
    phoneNumber: string,
    message: string,
    cost: number = 0
  ): Promise<FrontendSMS> {
    try {
      // Get current user and verify permissions
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Get user profile to check role and org_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('org_id, role')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('Could not fetch user profile');
      }

      // Verify user has appropriate role
      if (profile.role !== 'owner' && profile.role !== 'admin') {
        throw new Error('Access denied: Only owners and admins can log SMS messages');
      }

      // Insert the outbound SMS record
      const { data, error } = await supabase
        .from('frontend_sms')
        .insert({
          name: name,
          number: phoneNumber, // Recipient's phone number
          email: user.email || '', // Use the sender's email (auth user)
          message: message,
          cost: cost,
          direction: 'outbound',
          seen: true,
          created_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;

    } catch (error: any) {
      console.error('Error logging outbound SMS:', error);
      throw error;
    }
  }
};

export default frontendSMSService;
